/**
 * LiteRT.js helpers for browser EdgeYOLO / RTMDet / RF-DETR TFLite detection demos.
 */

let liteRtReady = null;

const LITERT_WASM_CDN = 'https://cdn.jsdelivr.net/npm/@litertjs/core@0.2.1/wasm/';

export async function ensureLiteRt() {
  if (!liteRtReady) {
    liteRtReady = (async () => {
      const mod = await import('@litertjs/core');
      await mod.loadLiteRt(LITERT_WASM_CDN);
      return mod;
    })();
  }
  return liteRtReady;
}

/** Fetch .tflite bytes (for label extraction + compile from buffer). */
export async function fetchTfliteBytes(tfliteUrl) {
  const res = await fetch(tfliteUrl);
  if (!res.ok) {
    throw new Error(`Failed to fetch TFLite model (${res.status})`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

/**
 * Compile a LiteRT model from a URL or already-fetched Uint8Array bytes.
 */
export async function loadDetectionModel(tfliteUrlOrBytes) {
  const { loadAndCompile } = await ensureLiteRt();
  const modelSource = tfliteUrlOrBytes;
  try {
    return await loadAndCompile(modelSource, { accelerator: 'wasm' });
  } catch (first) {
    // Retry once with webgpu if available.
    try {
      return await loadAndCompile(modelSource, { accelerator: 'webgpu' });
    } catch {
      throw first;
    }
  }
}

/**
 * Read the compiled model's primary input layout / size.
 * TFLite EdgeYOLO exports are almost always NHWC [1,H,W,3]; PyTorch-style
 * NCHW [1,3,H,W] is still supported when the signature says so.
 */
export function getModelInputSpec(model, fallbackSize) {
  const fbH = (fallbackSize && Number(fallbackSize[0])) || 640;
  const fbW = (fallbackSize && Number(fallbackSize[1])) || 640;
  let details;
  try {
    details = model && typeof model.getInputDetails === 'function'
      ? model.getInputDetails()[0]
      : null;
  } catch {
    details = null;
  }
  if (!details || !details.shape) {
    return {
      layout: 'nhwc',
      height: fbH,
      width: fbW,
      shape: [1, fbH, fbW, 3],
      dtype: 'float32',
    };
  }
  const dims = Array.from(details.shape).map((d) => Number(d));
  const spatial = dims.length === 4 ? dims.slice(1) : dims;
  let layout = 'nhwc';
  let height = fbH;
  let width = fbW;
  if (spatial.length >= 3) {
    const a = spatial[0];
    const b = spatial[1];
    const c = spatial[2];
    if (a > 0 && a <= 4 && b > 4 && c > 4) {
      // [C, H, W]
      layout = 'nchw';
      height = b > 0 ? b : fbH;
      width = c > 0 ? c : fbW;
    } else if (c > 0 && c <= 4 && a > 4 && b > 4) {
      // [H, W, C]
      layout = 'nhwc';
      height = a > 0 ? a : fbH;
      width = b > 0 ? b : fbW;
    } else {
      // Prefer NHWC (TFLite default) when ambiguous.
      layout = 'nhwc';
      height = a > 0 ? a : fbH;
      width = b > 0 ? b : fbW;
    }
  }
  // Prefer the exact signature shape (plain number[]) so LiteRT shape checks match.
  const shape = (dims.length === 4 && dims.every((d) => d > 0))
    ? dims.slice()
    : (layout === 'nchw' ? [1, 3, height, width] : [1, height, width, 3]);
  return {
    layout,
    height,
    width,
    shape,
    dtype: details.dtype || 'float32',
  };
}

/**
 * Letterbox draw source into a canvas sized inputW x inputH.
 * Returns Float32Array tensor data (NHWC or NCHW) + scale/pad for mapping boxes back.
 *
 * EdgeYOLO onnx2tf / TFLite exports expect float RGB in **0–255** (not 0–1).
 * Pad color 114 matches EdgeYOLO letterbox training/export.
 * RTMDet / RF-DETR TFLite via the same onnx2tf path typically also expect 0–255.
 */
export function letterboxDraw(source, inputW, inputH, opts) {
  const layout = (opts && opts.layout) === 'nchw' ? 'nchw' : 'nhwc';
  // normalize: 'uint8' → 0–255 (EdgeYOLO TFLite default); 'unit' → 0–1.
  const normalize = (opts && opts.normalize) === 'unit' ? 'unit' : 'uint8';
  const scalePixel = normalize === 'unit' ? (v) => v / 255 : (v) => v;
  const padColor = (opts && opts.padColor) != null ? opts.padColor : 114;
  const canvas = document.createElement('canvas');
  canvas.width = inputW;
  canvas.height = inputH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = `rgb(${padColor},${padColor},${padColor})`;
  ctx.fillRect(0, 0, inputW, inputH);

  const srcW = source.videoWidth || source.naturalWidth || source.width;
  const srcH = source.videoHeight || source.naturalHeight || source.height;
  const scale = Math.min(inputW / srcW, inputH / srcH);
  const newW = Math.round(srcW * scale);
  const newH = Math.round(srcH * scale);
  // Top-left pad matches EdgeYOLO training/export ``preproc`` (grey on the
  // right and/or bottom). Center pad shifts detections if the unmap uses a
  // different origin — the crop-on rectangular-model bug.
  const align = (opts && opts.align) === 'center' ? 'center' : 'top-left';
  const padX = align === 'center' ? Math.floor((inputW - newW) / 2) : 0;
  const padY = align === 'center' ? Math.floor((inputH - newH) / 2) : 0;
  ctx.drawImage(source, padX, padY, newW, newH);

  const imageData = ctx.getImageData(0, 0, inputW, inputH);
  const { data } = imageData;
  const plane = inputH * inputW;
  let tensor;
  if (layout === 'nchw') {
    tensor = new Float32Array(1 * 3 * plane);
    for (let i = 0; i < plane; i += 1) {
      const r = scalePixel(data[i * 4]);
      const g = scalePixel(data[i * 4 + 1]);
      const b = scalePixel(data[i * 4 + 2]);
      tensor[i] = r;
      tensor[plane + i] = g;
      tensor[2 * plane + i] = b;
    }
  } else {
    tensor = new Float32Array(1 * plane * 3);
    for (let i = 0; i < plane; i += 1) {
      const o = i * 3;
      tensor[o] = scalePixel(data[i * 4]);
      tensor[o + 1] = scalePixel(data[i * 4 + 1]);
      tensor[o + 2] = scalePixel(data[i * 4 + 2]);
    }
  }
  return {
    tensor, scale, padX, padY, srcW, srcH, layout, inputW, inputH, normalize,
  };
}

function nms(boxes, iouThreshold) {
  const sorted = [...boxes].sort((a, b) => b.score - a.score);
  const keep = [];
  while (sorted.length) {
    const best = sorted.shift();
    keep.push(best);
    for (let i = sorted.length - 1; i >= 0; i -= 1) {
      if (best.classId === sorted[i].classId && iou(best, sorted[i]) > iouThreshold) {
        sorted.splice(i, 1);
      }
    }
  }
  return keep;
}

function iou(a, b) {
  const x1 = Math.max(a.x1, b.x1);
  const y1 = Math.max(a.y1, b.y1);
  const x2 = Math.min(a.x2, b.x2);
  const y2 = Math.min(a.y2, b.y2);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const areaA = Math.max(0, a.x2 - a.x1) * Math.max(0, a.y2 - a.y1);
  const areaB = Math.max(0, b.x2 - b.x1) * Math.max(0, b.y2 - b.y1);
  const union = areaA + areaB - inter;
  return union <= 0 ? 0 : inter / union;
}

/**
 * INT8 TFLite often quantizes tiny normalised w/h to 0 (e.g. licence plates).
 * Native TFLite has the same zeroed dims; recover a visible box so LiteRT.js
 * can still show detections (aspect-aware minimum size in normalised space).
 */
export function repairCollapsedBoxSize(w, h, inputW, inputH) {
  const iw = inputW > 0 ? inputW : 640;
  const ih = inputH > 0 ? inputH : 640;
  const minW = 4 / iw;
  const minH = 4 / ih;
  let rw = w;
  let rh = h;
  const wOk = Number.isFinite(rw) && rw >= minW;
  const hOk = Number.isFinite(rh) && rh >= minH;
  if (wOk && hOk) {
    return { w: rw, h: rh, repaired: false };
  }
  if (!wOk && hOk) {
    // Landscape-ish default when width collapsed.
    rw = Math.max(minW, rh * 2.5);
  } else if (wOk && !hOk) {
    // Portrait / plate-like default when height collapsed (common INT8 failure).
    rh = Math.max(minH, rw / 3.5);
  } else {
    rw = minW;
    rh = minH;
  }
  return { w: rw, h: rh, repaired: true };
}

/**
 * Parse common YOLO / EdgeYOLO TFLite output layouts into axis-aligned boxes.
 */
function normalizeShape(shape) {
  if (!shape) {
    return null;
  }
  // LiteRT returns Int32Array; Array.isArray is false for typed arrays.
  if (typeof shape.length === 'number' && shape.length > 0) {
    return Array.from(shape).map((d) => Number(d));
  }
  return null;
}

/** Coerce LiteRT / TFLite output buffers (float32, int8, etc.) to Float32Array. */
export function coerceOutputFloat32(outputData) {
  if (outputData instanceof Float32Array) {
    return outputData;
  }
  if (outputData instanceof Float64Array) {
    return Float32Array.from(outputData);
  }
  // Int8/Uint8/Int32: values are already dequantized by LiteRT for *float32 I/O
  // models, OR raw quantized ints — convert element-wise to float for decode.
  if (ArrayBuffer.isView(outputData)) {
    return Float32Array.from(outputData);
  }
  if (Array.isArray(outputData)) {
    return Float32Array.from(outputData);
  }
  return new Float32Array(outputData);
}

export function decodeDetections(outputData, shape, opts) {
  const {
    labels = [],
    confThreshold = 0.35,
    inputW = 640,
    inputH = 640,
    repairCollapsed = false,
  } = opts || {};
  const flat = coerceOutputFloat32(outputData);
  let rows;
  let cols;
  const dims = normalizeShape(shape);
  if (dims && dims.length >= 2) {
    // Prefer [1, N, C] or [1, C, N]
    if (dims.length === 3) {
      const a = Number(dims[1]);
      const b = Number(dims[2]);
      // Transpose only when channels-first with many anchors: [1, C, N], C small, N large.
      if (a < b && a <= 64 && b > 64) {
        cols = a;
        rows = b;
        const transposed = new Float32Array(rows * cols);
        for (let r = 0; r < rows; r += 1) {
          for (let c = 0; c < cols; c += 1) {
            transposed[r * cols + c] = flat[c * rows + r];
          }
        }
        return decodeRows(transposed, rows, cols, labels, confThreshold, inputW, inputH, repairCollapsed);
      }
      rows = a;
      cols = b;
    } else if (dims.length === 2) {
      rows = Number(dims[0]);
      cols = Number(dims[1]);
    } else if (dims.length === 4) {
      // [1, N, C, 1] etc. — use last two meaningful dims
      rows = Number(dims[1]);
      cols = Number(dims[2]);
    }
  }
  if (!rows || !cols) {
    // Guess: last dim is channels if small
    const total = flat.length;
    const guessCols = labels.length > 0 ? 5 + labels.length : 6;
    if (total % guessCols === 0) {
      cols = guessCols;
      rows = total / cols;
    } else {
      cols = 6;
      rows = Math.floor(total / cols);
    }
  }
  return decodeRows(flat, rows, cols, labels, confThreshold, inputW, inputH, repairCollapsed);
}

function decodeRows(flat, rows, cols, labels, confThreshold, inputW, inputH, repairCollapsed) {
  const boxes = [];
  for (let r = 0; r < rows; r += 1) {
    const off = r * cols;
    if (off + cols > flat.length) {
      break;
    }
    let x;
    let y;
    let w;
    let h;
    let obj;
    let classId = 0;
    let score;

    if (cols >= 6) {
      // EdgeYOLO decode export: cx, cy, w, h, obj, class scores… (normalized 0–1)
      // OR post-NMS style: x1,y1,x2,y2,score,classId (often absolute pixels)
      const c0 = flat[off];
      const c1 = flat[off + 1];
      const c2 = flat[off + 2];
      const c3 = flat[off + 3];
      const c4 = flat[off + 4];
      const c5 = flat[off + 5];
      const maxCoord = Math.max(Math.abs(c0), Math.abs(c1), Math.abs(c2), Math.abs(c3));
      // Class-id layout only when coords look like pixels (not normalized YOLO).
      if (cols === 6 && c5LooksLikeClass(c5) && maxCoord > 1.5) {
        // x1,y1,x2,y2,score,classId
        score = c4;
        classId = Math.round(c5);
        if (score < confThreshold) {
          continue;
        }
        x = (c0 + c2) / 2;
        y = (c1 + c3) / 2;
        w = Math.abs(c2 - c0);
        h = Math.abs(c3 - c1);
      } else {
        obj = c4;
        let best = 0;
        let bestId = 0;
        for (let c = 5; c < cols; c += 1) {
          const s = flat[off + c];
          if (s > best) {
            best = s;
            bestId = c - 5;
          }
        }
        score = obj * best;
        classId = bestId;
        if (score < confThreshold) {
          continue;
        }
        x = c0;
        y = c1;
        w = c2;
        h = c3;
      }
    } else {
      continue;
    }

    // Coordinates may be normalized (0-1) or absolute pixels.
    const normalized = Math.max(x, y, w, h) <= 1.5;
    if (repairCollapsed && normalized) {
      const fixed = repairCollapsedBoxSize(w, h, inputW, inputH);
      w = fixed.w;
      h = fixed.h;
    }
    let x1;
    let y1;
    let x2;
    let y2;
    if (normalized) {
      x1 = (x - w / 2) * inputW;
      y1 = (y - h / 2) * inputH;
      x2 = (x + w / 2) * inputW;
      y2 = (y + h / 2) * inputH;
    } else {
      // Absolute pixel cxcywh (or already xyxy midpoints above).
      if (repairCollapsed) {
        const fixed = repairCollapsedBoxSize(w / inputW, h / inputH, inputW, inputH);
        w = fixed.w * inputW;
        h = fixed.h * inputH;
      }
      x1 = x - w / 2;
      y1 = y - h / 2;
      x2 = x + w / 2;
      y2 = y + h / 2;
    }
    const label = labels[classId] || `class ${classId}`;
    boxes.push({
      x1, y1, x2, y2, score, classId, label,
    });
  }
  return nms(boxes, 0.45).slice(0, 50);
}

function c5LooksLikeClass(v) {
  return Number.isFinite(v) && v >= 0 && v < 512 && Math.abs(v - Math.round(v)) < 1e-3;
}

export async function runDetection(model, nchwOrNhwcTensor, opts) {
  const { Tensor } = await ensureLiteRt();
  const spec = (opts && opts.inputSpec)
    || getModelInputSpec(model, [opts.inputH || 640, opts.inputW || 640]);
  const inputW = spec.width;
  const inputH = spec.height;
  // Plain array — Vue reactive Proxies around shape break LiteRT comparisons.
  const shape = Array.from(
    spec.shape || (
      spec.layout === 'nchw' ? [1, 3, inputH, inputW] : [1, inputH, inputW, 3]
    ),
  ).map((d) => Number(d));
  // Own the buffer so Safari cannot pass a detached ImageData-backed view.
  const owned = nchwOrNhwcTensor instanceof Float32Array
    ? new Float32Array(nchwOrNhwcTensor)
    : new Float32Array(nchwOrNhwcTensor);
  let inputTensor = new Tensor(owned, shape);
  let outputs;
  try {
    // Match model accelerator (wasm / webgpu) before run — required on Safari.
    const target = model.accelerator || 'wasm';
    if (inputTensor.accelerator !== target && typeof inputTensor.copyTo === 'function') {
      const moved = await inputTensor.copyTo(target);
      inputTensor.delete();
      inputTensor = moved;
    }
    outputs = await model.run(inputTensor);
  } finally {
    if (inputTensor && typeof inputTensor.delete === 'function') {
      inputTensor.delete();
    }
  }
  const outList = Array.isArray(outputs) ? outputs : [outputs];
  const out = outList[0];
  let data;
  let shapeOut;
  let wasmOut = null;
  try {
    // Outputs may live on webgpu; toTypedArray requires wasm.
    if (out.accelerator && out.accelerator !== 'wasm' && typeof out.moveTo === 'function') {
      wasmOut = await out.moveTo('wasm');
    } else {
      wasmOut = out;
    }
    if (typeof wasmOut.toTypedArray === 'function') {
      data = wasmOut.toTypedArray();
    } else if (typeof wasmOut.data === 'function') {
      data = await wasmOut.data();
    } else {
      throw new Error('LiteRT output tensor has no readable data API');
    }
    shapeOut = (wasmOut.type && wasmOut.type.layout && wasmOut.type.layout.dimensions)
      || wasmOut.shape
      || (typeof wasmOut.getShape === 'function' ? wasmOut.getShape() : null);
  } finally {
    outList.forEach((o) => {
      if (o && typeof o.delete === 'function') {
        o.delete();
      }
    });
    if (wasmOut && wasmOut !== out && typeof wasmOut.delete === 'function') {
      wasmOut.delete();
    }
  }
  return decodeDetections(data, shapeOut, {
    ...opts,
    inputW,
    inputH,
  });
}

const IMAGENET_MEAN = [0.485, 0.456, 0.406];
const IMAGENET_STD = [0.229, 0.224, 0.225];

/**
 * Softmax over a flat score vector (in-place safe; returns new array).
 */
export function softmax(logits) {
  const arr = Array.from(logits || []).map((v) => Number(v));
  if (!arr.length) {
    return [];
  }
  let max = -Infinity;
  for (let i = 0; i < arr.length; i += 1) {
    if (arr[i] > max) {
      max = arr[i];
    }
  }
  const exps = arr.map((v) => Math.exp(v - max));
  const sum = exps.reduce((a, b) => a + b, 0) || 1;
  return exps.map((v) => v / sum);
}

/**
 * Decode classifier TFLite output into top-K {classId, label, score} rows.
 * Applies softmax when values do not already look like probabilities.
 */
export function decodeTopK(outputData, shape, opts) {
  const {
    labels = [],
    topK = 3,
  } = opts || {};
  const flat = coerceOutputFloat32(outputData);
  let scores = Array.from(flat);
  const dims = normalizeShape(shape);
  // Prefer last dim as class axis: [1, C] or [1,1,C] etc.
  if (dims && dims.length >= 1) {
    const last = Number(dims[dims.length - 1]);
    if (last > 1 && last <= scores.length) {
      scores = scores.slice(0, last);
    }
  }
  if (!scores.length) {
    return [];
  }
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const sum = scores.reduce((a, b) => a + b, 0);
  // Already probabilities (softmax) if in [0,1] and roughly sum to 1.
  const looksLikeProbs = min >= -1e-5 && max <= 1 + 1e-5 && Math.abs(sum - 1) < 0.05;
  const probs = looksLikeProbs ? scores.map((s) => Math.max(0, s)) : softmax(scores);
  const indexed = probs.map((score, classId) => ({
    classId,
    label: labels[classId] || `class ${classId}`,
    score,
  }));
  indexed.sort((a, b) => b.score - a.score);
  return indexed.slice(0, Math.max(1, topK));
}

/**
 * Center-crop / letterbox-free resize for classifiers (fastai-style square resize).
 * normalize: 'unit' → 0–1; 'imagenet' → ImageNet mean/std on 0–1 RGB; 'uint8' → 0–255.
 */
export function classificationDraw(source, inputW, inputH, opts) {
  const layout = (opts && opts.layout) === 'nchw' ? 'nchw' : 'nhwc';
  const normalize = (opts && opts.normalize) || 'imagenet';
  const canvas = document.createElement('canvas');
  canvas.width = inputW;
  canvas.height = inputH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgb(0,0,0)';
  ctx.fillRect(0, 0, inputW, inputH);

  const srcW = source.videoWidth || source.naturalWidth || source.width;
  const srcH = source.videoHeight || source.naturalHeight || source.height;
  // Cover-style resize (center crop) matches typical fastai Resize behaviour better than letterbox.
  const scale = Math.max(inputW / srcW, inputH / srcH);
  const newW = Math.round(srcW * scale);
  const newH = Math.round(srcH * scale);
  const padX = Math.floor((inputW - newW) / 2);
  const padY = Math.floor((inputH - newH) / 2);
  ctx.drawImage(source, padX, padY, newW, newH);

  const imageData = ctx.getImageData(0, 0, inputW, inputH);
  const { data } = imageData;
  const plane = inputH * inputW;
  const scalePixel = (v, c) => {
    const unit = v / 255;
    if (normalize === 'uint8') {
      return v;
    }
    if (normalize === 'unit') {
      return unit;
    }
    // imagenet
    return (unit - IMAGENET_MEAN[c]) / IMAGENET_STD[c];
  };
  let tensor;
  if (layout === 'nchw') {
    tensor = new Float32Array(1 * 3 * plane);
    for (let i = 0; i < plane; i += 1) {
      tensor[i] = scalePixel(data[i * 4], 0);
      tensor[plane + i] = scalePixel(data[i * 4 + 1], 1);
      tensor[2 * plane + i] = scalePixel(data[i * 4 + 2], 2);
    }
  } else {
    tensor = new Float32Array(1 * plane * 3);
    for (let i = 0; i < plane; i += 1) {
      const o = i * 3;
      tensor[o] = scalePixel(data[i * 4], 0);
      tensor[o + 1] = scalePixel(data[i * 4 + 1], 1);
      tensor[o + 2] = scalePixel(data[i * 4 + 2], 2);
    }
  }
  return {
    tensor, scale, padX, padY, srcW, srcH, layout, inputW, inputH, normalize,
  };
}

/**
 * Run a classification TFLite model; returns top-K class predictions.
 */
export async function runClassification(model, nchwOrNhwcTensor, opts) {
  const { Tensor } = await ensureLiteRt();
  const spec = (opts && opts.inputSpec)
    || getModelInputSpec(model, [opts.inputH || 224, opts.inputW || 224]);
  const inputW = spec.width;
  const inputH = spec.height;
  const shape = Array.from(
    spec.shape || (
      spec.layout === 'nchw' ? [1, 3, inputH, inputW] : [1, inputH, inputW, 3]
    ),
  ).map((d) => Number(d));
  const owned = nchwOrNhwcTensor instanceof Float32Array
    ? new Float32Array(nchwOrNhwcTensor)
    : new Float32Array(nchwOrNhwcTensor);
  let inputTensor = new Tensor(owned, shape);
  let outputs;
  try {
    const target = model.accelerator || 'wasm';
    if (inputTensor.accelerator !== target && typeof inputTensor.copyTo === 'function') {
      const moved = await inputTensor.copyTo(target);
      inputTensor.delete();
      inputTensor = moved;
    }
    outputs = await model.run(inputTensor);
  } finally {
    if (inputTensor && typeof inputTensor.delete === 'function') {
      inputTensor.delete();
    }
  }
  const outList = Array.isArray(outputs) ? outputs : [outputs];
  const out = outList[0];
  let data;
  let shapeOut;
  let wasmOut = null;
  try {
    if (out.accelerator && out.accelerator !== 'wasm' && typeof out.moveTo === 'function') {
      wasmOut = await out.moveTo('wasm');
    } else {
      wasmOut = out;
    }
    if (typeof wasmOut.toTypedArray === 'function') {
      data = wasmOut.toTypedArray();
    } else if (typeof wasmOut.data === 'function') {
      data = await wasmOut.data();
    } else {
      throw new Error('LiteRT output tensor has no readable data API');
    }
    shapeOut = (wasmOut.type && wasmOut.type.layout && wasmOut.type.layout.dimensions)
      || wasmOut.shape
      || (typeof wasmOut.getShape === 'function' ? wasmOut.getShape() : null);
  } finally {
    outList.forEach((o) => {
      if (o && typeof o.delete === 'function') {
        o.delete();
      }
    });
    if (wasmOut && wasmOut !== out && typeof wasmOut.delete === 'function') {
      wasmOut.delete();
    }
  }
  return decodeTopK(data, shapeOut, {
    labels: (opts && opts.labels) || [],
    topK: (opts && opts.topK) != null ? opts.topK : 3,
  });
}

